import { isBefore, parseISO, startOfDay } from "date-fns";
import { formatDate } from "@/lib/formatters/DateFormatter";
import type { RebateModalMode } from "../components/HandleRebateFormModal";

interface GoalPeriod {
  validTo?: string | null;
}

/**
 * A goal is stamped REACHED the moment the achieved volume crosses the
 * target, which on a yearly rebate can happen in March. Everything else in
 * the payout flow asks about volume and nothing asks about time, so a
 * rebate that is still running looks exactly like one that is finished.
 *
 * The period is ongoing through the whole of its last day, so validTo is
 * compared as a day rather than as an instant.
 */
export const isGoalPeriodOngoing = (
  goal: GoalPeriod | undefined | null,
  today: Date = new Date()
): boolean => {
  if (!goal?.validTo) {
    return false;
  }

  const periodEnd = parseISO(goal.validTo);
  if (Number.isNaN(periodEnd.getTime())) {
    return false;
  }

  return !isBefore(startOfDay(periodEnd), startOfDay(today));
};

/**
 * Settling early is allowed - a customer leaving mid-period has to be paid
 * out somehow - so this only says what it is about to cost. Paying and
 * releasing carry opposite risks, which is why each has its own wording
 * rather than both reporting that the period is still running.
 */
export const periodOngoingWarning = (
  mode: RebateModalMode,
  goal: GoalPeriod | undefined | null,
  today: Date = new Date()
): string | undefined => {
  if (!isGoalPeriodOngoing(goal, today)) {
    return undefined;
  }

  const runsUntil = `This rebate runs until ${formatDate(goal!.validTo!)}.`;

  return mode === "RELEASE"
    ? `${runsUntil} Releasing now gives up the reserved amount while the rebate can still reach its target.`
    : `${runsUntil} Paying out now settles it on what it has achieved so far, and volume booked before the period ends can still change what it earns.`;
};
